import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X as XIcon } from 'lucide-react';
import Button from '../../components/ui/Button';
import { getConsentText } from '../../services/careerService';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.h2`
  font-size: 18px;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseX = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.dark.little};
  padding: 4px;
  flex-shrink: 0;
  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const Body = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
`;

const Intro = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 18px 0;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 6px 0;
`;

const SectionBody = styled.p`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  white-space: pre-line;
`;

const Foot = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
  display: flex;
  justify-content: flex-end;
`;

// #72 — yerel CloseBtn kaldırıldı, paylaşılan Button'a bağlandı (bkz.
// t6-report.md).

const SECTIONS = ['controller', 'data', 'purpose', 'legalBasis', 'transfer', 'retention', 'rights', 'contact'];

export default function ConsentModal({ companyName, tenantSlug, onClose }) {
  const { t } = useTranslation();
  const company = companyName || t('career.kvkk.companyFallback');
  // Tenant kendi aydınlatma metnini tanımlamışsa o gösterilir; yoksa (null)
  // standart yerelleştirilmiş metin. Yükleme bitene dek içerik basılmaz ki
  // özel metinli tenant'ta standart metin bir an bile görünmesin.
  const [customText, setCustomText] = useState(null);
  const [loading, setLoading] = useState(!!tenantSlug);

  useEffect(() => {
    if (!tenantSlug) return;
    let mounted = true;
    getConsentText(tenantSlug)
      .then((res) => { if (mounted) setCustomText(res.data?.data?.text || null); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [tenantSlug]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <Head>
          <Title>{t('career.kvkk.title')}</Title>
          <CloseX type="button" onClick={onClose} aria-label={t('career.kvkk.close')}>
            <XIcon size={20} />
          </CloseX>
        </Head>
        <Body>
          {loading ? null : customText ? (
            <SectionBody>{customText}</SectionBody>
          ) : (
            <>
              <Intro>{t('career.kvkk.intro', { company })}</Intro>
              {SECTIONS.map((s) => (
                <React.Fragment key={s}>
                  <SectionTitle>{t(`career.kvkk.section.${s}.title`)}</SectionTitle>
                  <SectionBody>{t(`career.kvkk.section.${s}.body`, { company })}</SectionBody>
                </React.Fragment>
              ))}
            </>
          )}
        </Body>
        <Foot>
          <Button type="button" onClick={onClose}>{t('career.kvkk.close')}</Button>
        </Foot>
      </Panel>
    </Overlay>
  );
}
